import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, Heart, Pin, PinOff, Lock, Unlock, Trash2, Pencil, Eye, MessageSquare, Mail, Quote } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, Spinner, Avatar, RoleBadge, Time, Markdown, Btn, Empty } from "@/forum/ui";
import { ReplyBox, Editor } from "@/forum/Editor";
import { Pager } from "./CategoryPage";

const IconBtn = ({ onClick, title, active, children, testId, danger }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    data-testid={testId}
    className={`inline-flex h-8 items-center gap-1.5 rounded-full border px-3 font-mono text-[11px] transition-colors ${
      danger ? "border-red-500/30 text-red-300 hover:bg-red-500/10" : active ? "border-[rgba(94,177,255,0.6)] bg-[rgba(94,177,255,0.12)] text-[var(--glow)]" : "border-white/10 text-white/60 hover:border-white/30 hover:text-white"
    }`}
  >
    {children}
  </button>
);

const Post = ({ p, index, isMod, onLike, onDelete, onEdit, onQuote, me }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(p.content);
  const own = me && p.author?.id === me.id;
  return (
    <motion.article initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay: Math.min(index, 8) * 0.04 }} data-testid={`post-${p.id}`} id={`post-${p.id}`}>
      <Panel className="grid gap-5 p-5 sm:grid-cols-[180px_1fr] sm:p-6">
        <div className="flex items-center gap-3 sm:flex-col sm:items-start sm:border-r sm:border-white/[0.06] sm:pr-5">
          <Avatar user={p.author} size="lg" />
          <div className="min-w-0">
            <Link to={`/forum/u/${p.author?.username}`} className="block truncate font-display text-base font-bold hover:text-[var(--glow)]" data-testid="post-author">{p.author?.username}</Link>
            <RoleBadge role={p.author?.role} label={p.author?.role_label} className="mt-1" />
            <p className="mt-2 font-mono text-[10px] text-white/35">{p.author?.post_count} hozzászólás</p>
          </div>
        </div>
        <div className="min-w-0">
          <div className="mb-4 flex items-center justify-between font-mono text-[11px] text-white/40">
            <span>#{index + 1} · <Time iso={p.created_at} />{p.edited_at && <span> · szerkesztve</span>}</span>
          </div>
          {editing ? (
            <div className="space-y-3">
              <Editor value={draft} onChange={setDraft} rows={8} testId={`edit-editor-${p.id}`} />
              <div className="flex justify-end gap-2">
                <Btn variant="ghost" onClick={() => { setEditing(false); setDraft(p.content); }} data-testid="edit-cancel">Mégse</Btn>
                <Btn onClick={async () => { await onEdit(p.id, draft); setEditing(false); }} data-testid="edit-save">Mentés</Btn>
              </div>
            </div>
          ) : (
            <div data-testid="post-content"><Markdown>{p.content}</Markdown></div>
          )}
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <IconBtn onClick={() => onLike(p.id)} active={p.liked} title="Kedvelés" testId={`like-${p.id}`}>
              <Heart size={12} className={p.liked ? "fill-current" : ""} /> {p.likes}
            </IconBtn>
            {me && <IconBtn onClick={() => onQuote(p)} title="Idézés" testId={`quote-${p.id}`}><Quote size={12} /> Idézés</IconBtn>}
            {(own || isMod) && !editing && <IconBtn onClick={() => setEditing(true)} title="Szerkesztés" testId={`edit-${p.id}`}><Pencil size={12} /></IconBtn>}
            {(own || isMod) && index > 0 && <IconBtn onClick={() => onDelete(p.id)} title="Törlés" danger testId={`delete-post-${p.id}`}><Trash2 size={12} /></IconBtn>}
          </div>
        </div>
      </Panel>
    </motion.article>
  );
};

export default function ThreadPage() {
  const { id } = useParams();
  const [sp, setSp] = useSearchParams();
  const page = Number(sp.get("page") || 1);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [quote, setQuote] = useState("");
  const { user, refresh } = useAuth();
  const nav = useNavigate();

  const load = useCallback(() => {
    api.get(`/forum/threads/${id}`, { params: { page } }).then((r) => setData(r.data)).catch((e) => setErr(errorText(e)));
  }, [id, page]);

  useEffect(() => { if (user !== null) load(); }, [load, user]);

  if (err) return <Empty text={err} testId="thread-error" />;
  if (!data) return <Spinner />;
  const { thread: t, category: c, posts } = data;

  const act = async (fn, ok) => {
    try { await fn(); ok && toast.success(ok); load(); } catch (e) { toast.error(errorText(e)); }
  };
  const patch = (body, ok) => act(() => api.patch(`/forum/threads/${id}`, body), ok);
  const reply = async (content) => {
    setBusy(true);
    await act(() => api.post(`/forum/threads/${id}/posts`, { content }), "Válasz elküldve");
    setBusy(false);
    setQuote("");
    refresh();
  };
  const like = async (pid) => {
    if (!user) return nav("/login", { state: { from: `/forum/t/${id}` } });
    try {
      const { data: r } = await api.post(`/forum/posts/${pid}/like`);
      setData((d) => ({ ...d, posts: d.posts.map((p) => (p.id === pid ? { ...p, liked: r.liked, likes: r.likes } : p)) }));
    } catch (e) { toast.error(errorText(e)); }
  };
  const delPost = (pid) => window.confirm("Biztosan törlöd a hozzászólást?") && act(() => api.delete(`/forum/posts/${pid}`), "Hozzászólás törölve");
  const editPost = (pid, content) => act(() => api.put(`/forum/posts/${pid}`, { content }), "Mentve");
  const delThread = () => window.confirm("Biztosan törlöd az egész témát?") && act(async () => { await api.delete(`/forum/threads/${id}`); nav(`/forum/c/${c.slug}`); }, "Téma törölve");
  const onQuote = (p) => {
    setQuote(`> **${p.author?.username}** írta:\n> ${p.content.split("\n").join("\n> ")}\n\n`);
    document.getElementById("reply")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="space-y-6" data-testid="thread-page">
      <Link to={`/forum/c/${c.slug}`} className="inline-flex items-center gap-1 font-mono text-[11px] tracking-widest text-white/50 hover:text-white" data-testid="back-to-category"><ChevronLeft size={12} />{c.name.toUpperCase()}</Link>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            {t.pinned && <span className="inline-flex items-center gap-1 rounded-full border border-[rgba(94,177,255,0.5)] px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-[var(--glow)]" data-testid="thread-pinned-badge"><Pin size={10} />Kitűzve</span>}
            {t.locked && <span className="inline-flex items-center gap-1 rounded-full border border-white/20 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-white/60" data-testid="thread-locked-badge"><Lock size={10} />Zárolva</span>}
          </div>
          <h1 className="mt-2 font-display text-2xl font-bold sm:text-4xl" data-testid="thread-title">{t.title}</h1>
          <p className="mt-2 flex flex-wrap items-center gap-3 font-mono text-[11px] text-white/40">
            <span>{t.author?.username} · <Time iso={t.created_at} /></span>
            <span className="inline-flex items-center gap-1"><Eye size={12} />{t.views}</span>
            <span className="inline-flex items-center gap-1"><MessageSquare size={12} />{t.reply_count}</span>
          </p>
        </div>
        {(data.is_mod || (user && t.author?.id === user.id)) && (
          <div className="flex flex-wrap gap-2" data-testid="thread-mod-actions">
            {data.is_mod && <IconBtn onClick={() => patch({ pinned: !t.pinned }, t.pinned ? "Kitűzés megszüntetve" : "Téma kitűzve")} active={t.pinned} testId="toggle-pin">{t.pinned ? <PinOff size={12} /> : <Pin size={12} />} {t.pinned ? "Levétel" : "Kitűzés"}</IconBtn>}
            {data.is_mod && <IconBtn onClick={() => patch({ locked: !t.locked }, t.locked ? "Téma feloldva" : "Téma zárolva")} active={t.locked} testId="toggle-lock">{t.locked ? <Unlock size={12} /> : <Lock size={12} />} {t.locked ? "Feloldás" : "Zárolás"}</IconBtn>}
            <IconBtn onClick={delThread} danger testId="delete-thread"><Trash2 size={12} /> Téma törlése</IconBtn>
          </div>
        )}
      </div>

      <div className="space-y-4">
        {posts.map((p, i) => (
          <Post key={p.id} p={p} index={(data.page - 1) * 20 + i} isMod={data.is_mod} me={user} onLike={like} onDelete={delPost} onEdit={editPost} onQuote={onQuote} />
        ))}
      </div>
      <Pager page={data.page} pages={data.pages} onChange={(p) => setSp({ page: p })} />

      <div id="reply">
        {data.can_reply ? (
          <Panel className="p-5 sm:p-6" data-testid="reply-panel">
            <p className="eyebrow mb-4">Válasz</p>
            <ReplyBox key={quote} onSubmit={reply} busy={busy} initial={quote} />
          </Panel>
        ) : t.locked ? (
          <Empty text="Ez a téma zárolva van, nem lehet válaszolni." testId="locked-notice" />
        ) : !user ? (
          <Panel className="flex flex-col items-center gap-3 p-8 text-center" data-testid="login-to-reply">
            <Mail size={18} className="text-[var(--glow)]" />
            <p className="text-sm text-white/60">Jelentkezz be a válaszoláshoz.</p>
            <Link to="/login" className="btn-glow rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-black">Belépés</Link>
          </Panel>
        ) : <Empty text="Ebbe a kategóriába nincs írási jogod." testId="no-reply-permission" />}
      </div>
    </div>
  );
}
